import { type MRT_ColumnSizingState, type MRT_VisibilityState } from 'material-react-table'
import { ConfirmExportDataLimitScope } from 'src/main/Main.scopes'
import { Observable, ObservableArray, Scope, observe } from 'wdc-cube'
import { GroupRow } from '../admin/group-manager/gm_scopes'
import { UserRow } from '../admin/user-manager/um_scopes'
import {
    FilterSelectOption,
    JunctionMode,
    NumberOperator,
    SelectOperator,
    // Concrete
    SituacaoCadastral,
    StringKeywordOperator,
    StringOperator,
    type ColumnFilter,
    type ColumnSort,
    // Abstract
    type EmpresaBean,
    type FilterCatalogOption,
    type GeoLocationMode,
    type PESRow,
    type PRFRow,
    type QSABean
} from './tcm_service'

export {
    FilterCatalogOption,
    JunctionMode,
    NumberOperator,
    PESRow,
    PRFRow,
    SituacaoCadastral,
    StringKeywordOperator,
    StringOperator,
    type GeoLocationMode
}

export type AppliedFilterPart = {
    prefix?: string
    tags: string[]
    hiddenCount: number
}

export type AppliedFilterSummary = {
    caption: string
    parts: AppliedFilterPart[]
}

@Observable
export class TextualSearchScope extends Scope {
    @observe() text = ''
    @observe() waiting = false

    onSearch = Scope.ASYNC_ACTION
}

@Observable
export class HeaderScope extends Scope {
    textualSearch?: TextualSearchScope
}

@Observable
export class ValueScope extends Scope {
    @observe() caption = ''
    @observe() captionHint = ''
    @observe() loaded = false
    @observe() value = 0
    @observe() selected = false

    onClicked?: typeof Scope.ASYNC_ACTION
}

@Observable
export class ChartFilialMatrizScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() numMatrizes = 0
    @observe() numFiliais = 0

    @observe() matrizesSelected = false
    @observe() filiaisSelected = false

    onItemClicked = Scope.ASYNC_ACTION_ONE<boolean>()
}

@Observable
export class ChartSituacaoCadastralScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() baixada = 0
    @observe() ativa = 0
    @observe() inapta = 0
    @observe() supensa = 0
    @observe() nula = 0

    selectedMap = new Map<SituacaoCadastral, boolean>()

    onItemClicked = Scope.ASYNC_ACTION_ONE<SituacaoCadastral>()
}

export type NaturezaJuridicaBean = {
    codigo: number
    descricao: string
    quantidade: number
    selected?: boolean
}

export type EmpresasAtivasPorUFBean = {
    codigo: string
    quantidade: number
    selected?: boolean
}

@Observable
export class ChartNaturezaJuridicaScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as NaturezaJuridicaBean[]

    onItemClicked = Scope.ASYNC_ACTION_ONE<number>()
}

@Observable
export class ChartEmpresasAtivasPorUfScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as EmpresasAtivasPorUFBean[]

    onItemClicked = Scope.ASYNC_ACTION_ONE<string>()
}

export type TableAtividadeEconomicaRow = {
    id: string
    descricao: string
    qtdEmpresas: number
    qtdFuncionarios: number
    selected?: boolean
}

@Observable
export class TableAtividadeEconomicaScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as TableAtividadeEconomicaRow[]

    onItemClicked = Scope.ASYNC_ACTION_ONE<string>()
}

export type DataZoom = {
    startValue: number
    endValue: number
}

@Observable
export class ChartEmpresasAbertasFechadasScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as [string, number, number][]

    onRestore = Scope.ASYNC_ACTION
    onDataZoom = Scope.ASYNC_ACTION_ONE<DataZoom>()
}

@Observable
export class ChartFaixasScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as [number, string, boolean][]

    onItemClicked = Scope.ASYNC_ACTION_ONE<number>()
}

export type EmpresaRow = EmpresaBean & { selected?: boolean }

export type CrmExportOption = {
    connectionId: number
    crmType: string
    name: string
}

@Observable
export class TableEmpresaScope extends Scope {
    exportDataLimitScope = new ConfirmExportDataLimitScope()

    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as EmpresaRow[]
    @observe() datasetVersion = 0
    @observe() downloadEnabled = true
    @observe() checkedRowCount = 0
    @observe() working = false
    @observe() crmExportOptions: CrmExportOption[] = []
    @observe() pageIndex = 0
    @observe() pageSize = 100
    @observe() rowCount = 1
    @observe() sorting: ColumnSort[] = []
    @observe() filters: ColumnFilter[] = []
    @observe() exportColumns: string[] = []
    @observe() columnSizing: MRT_ColumnSizingState = {}
    @observe() columnVisibility: MRT_VisibilityState = {}

    readonly total = {
        func_qtd: 0,
        faturamento_estimado: 0,
        faturamento_estimado_grupo: 0
    }

    onSelectionChange = Scope.ASYNC_ACTION_ONE<Map<string, boolean>>()
    onFiltrationChange = Scope.ASYNC_ACTION_ONE<ColumnFilter[]>()
    onPaginationChange = Scope.ASYNC_ACTION_TWO<number, number>()
    onSortingChange = Scope.ASYNC_ACTION_ONE<ColumnSort[]>()
    onDownloadClick = Scope.ASYNC_ACTION_ONE<string[]>()
    setColumnSizing = Scope.ASYNC_ACTION_ONE<MRT_ColumnSizingState>()
    onColumnsVisibilityChange = Scope.ASYNC_ACTION_ONE<MRT_VisibilityState>()
    onCopyCnpj = Scope.ASYNC_ACTION
    onOpenCorporateRelations = Scope.ASYNC_ACTION
    onCrmExport = Scope.ASYNC_ACTION_ONE<number>()
}

export type QSARow = QSABean & { selected?: boolean }

@Observable
export class TableQSAScope extends Scope {
    exportDataLimitScope = new ConfirmExportDataLimitScope()

    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as QSARow[]
    @observe() datasetVersion = 0
    @observe() downloadEnabled = true
    @observe() working = false
    @observe() pageIndex = 0
    @observe() pageSize = 100
    @observe() rowCount = 1
    @observe() sorting: ColumnSort[] = []
    @observe() filters: ColumnFilter[] = []
    @observe() exportColumns: string[] = []
    @observe() columnSizing: MRT_ColumnSizingState = {}

    readonly total = {
        func_qtd: 0,
        faturamento_estimado: 0,
        faturamento_estimado_grupo: 0
    }

    onSelectionChange = Scope.ASYNC_ACTION_ONE<Map<string, boolean>>()
    onFiltrationChange = Scope.ASYNC_ACTION_ONE<ColumnFilter[]>()
    onPaginationChange = Scope.ASYNC_ACTION_TWO<number, number>()
    onSortingChange = Scope.ASYNC_ACTION_ONE<ColumnSort[]>()
    onDownloadClick = Scope.ASYNC_ACTION_ONE<string[]>()
    setColumnSizing = Scope.ASYNC_ACTION_ONE<MRT_ColumnSizingState>()
}

@Observable
export class TablePRFScope extends Scope {
    exportDataLimitScope = new ConfirmExportDataLimitScope()

    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as PRFRow[]
    @observe() datasetVersion = 0
    @observe() downloadEnabled = true
    @observe() working = false
    @observe() pageIndex = 0
    @observe() pageSize = 100
    @observe() rowCount = 1
    @observe() sorting: ColumnSort[] = []
    @observe() filters: ColumnFilter[] = []
    @observe() exportColumns: string[] = []
    @observe() columnSizing: MRT_ColumnSizingState = {}

    onSelectionChange = Scope.ASYNC_ACTION_ONE<Map<string, boolean>>()
    onFiltrationChange = Scope.ASYNC_ACTION_ONE<ColumnFilter[]>()
    onPaginationChange = Scope.ASYNC_ACTION_TWO<number, number>()
    onSortingChange = Scope.ASYNC_ACTION_ONE<ColumnSort[]>()
    onDownloadClick = Scope.ASYNC_ACTION_ONE<string[]>()
    setColumnSizing = Scope.ASYNC_ACTION_ONE<MRT_ColumnSizingState>()
}

@Observable
export class TablePESScope extends Scope {
    exportDataLimitScope = new ConfirmExportDataLimitScope()

    @observe() caption = ''
    @observe() loaded = false
    @observe() dataset = [] as PESRow[]
    @observe() datasetVersion = 0
    @observe() downloadEnabled = true
    @observe() working = false
    @observe() pageIndex = 0
    @observe() pageSize = 100
    @observe() rowCount = 1
    @observe() sorting: ColumnSort[] = []
    @observe() filters: ColumnFilter[] = []
    @observe() exportColumns: string[] = []
    @observe() columnSizing: MRT_ColumnSizingState = {}

    onSelectionChange = Scope.ASYNC_ACTION_ONE<Map<string, boolean>>()
    onFiltrationChange = Scope.ASYNC_ACTION_ONE<ColumnFilter[]>()
    onPaginationChange = Scope.ASYNC_ACTION_TWO<number, number>()
    onSortingChange = Scope.ASYNC_ACTION_ONE<ColumnSort[]>()
    onDownloadClick = Scope.ASYNC_ACTION_ONE<string[]>()
    setColumnSizing = Scope.ASYNC_ACTION_ONE<MRT_ColumnSizingState>()
}

@Observable
export class SliderScope extends Scope {
    @observe() min = 10
    @observe() max = 1000
    @observe() step = 10
    @observe() value = 50
    @observe() unit: 'km' | 'm' = 'm'
    @observe() visible = false

    onValueChanged = Scope.ASYNC_ACTION_NUMBER
}

@Observable
export class MapCardScope extends Scope {
    @observe() caption = ''
    @observe() expanded = false
    @observe() showClearButton = false
    @observe() mode: GeoLocationMode = 'route'

    readonly slider = new SliderScope()

    onExpandClick = Scope.ASYNC_ACTION
    onShrunkClick = Scope.ASYNC_ACTION
    onMapChanged = Scope.ASYNC_ACTION_ONE<L.Map | null>()
    onClearFilters = Scope.ASYNC_ACTION
    onSetAreaMode = Scope.ASYNC_ACTION
    onSetRouteMode = Scope.ASYNC_ACTION
    onSetCircleMode = Scope.ASYNC_ACTION
    onSetMapMode = Scope.ASYNC_ACTION
}

@Observable
export class StringParcelFilterScope extends Scope {
    uid = ''
    junctionMode: JunctionMode = JunctionMode.AND

    @observe() operator = StringOperator.contains
    @observe() value = ''
    @observe() valueVisible = true
    @observe() hasInOption = false
    @observe() acceptBlankValues = true
    @observe() options: string[] = []
    @observe() extra?: Scope

    onClear = Scope.ASYNC_ACTION
    onEnterKeyPressed = Scope.ASYNC_ACTION
    onOperatorChange = Scope.ASYNC_ACTION_ONE<StringOperator>()
    onValueChange = Scope.ASYNC_ACTION_STRING
    onOptionSelected = Scope.ASYNC_ACTION_STRING
}

export interface IFilterScope extends Scope {
    order: number
    visible: boolean
    expanded: boolean
    caption: string
    onExpandedChange(expanded: boolean): Promise<void>
}

@Observable
export class FilterExtraAeScope extends Scope {
    @observe() domain = 1

    onChange = Scope.ASYNC_ACTION_NUMBER
}

@Observable
export class FilterExtraCboScope extends Scope {
    @observe() isFilterQuantidade = false
    @observe() extraMode?: JunctionMode
    @observe() extraOperator?: NumberOperator = NumberOperator.in_range
    @observe() extraValue?: string | number
    @observe() extraTo?: string | number

    onChangeIsFiltroQuantidade = Scope.ASYNC_ACTION_BOOLEAN
    onExtraClear = Scope.ASYNC_ACTION
    onExtraOperatorChange = Scope.ASYNC_ACTION_ONE<NumberOperator>()
    onExtraValueChange = Scope.ASYNC_ACTION_TWO<string, 'from' | 'to'>()
}

@Observable
export class StringFilterScope extends Scope implements IFilterScope {
    @observe() caption = ''
    // Fechado de saída, como o acordeão não controlado se comportava antes de o escopo passar
    // a mandar nele. Abre no clique do usuário ou quando um item é adicionado pelo +.
    @observe() expanded = false
    @observe() visible = false
    order = 0
    readonly conjunction = new ObservableArray<StringParcelFilterScope>(this)
    readonly disjunction = new ObservableArray<StringParcelFilterScope>(this)

    onAddFilter = Scope.ASYNC_ACTION_ONE<JunctionMode>()
    onExpandedChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onClearAllFilters = Scope.ASYNC_ACTION
}

@Observable
export class MultipleOptionStringKeywordParcelFilterScope extends Scope {
    uid = ''
    junctionMode: JunctionMode = JunctionMode.AND

    @observe() operator = StringKeywordOperator.is_in
    @observe() value = [] as string[]
    @observe() valueVisible = true
    @observe() acceptBlankValues = true
    @observe() extra?: Scope
    @observe() extraPosition: 'begin' | 'end' = 'begin'
    @observe() options: KeywordOption[] = []
    @observe() optionsLoading = false
    @observe() optionsError = ''

    getValueInOptionType = (): KeywordOption[] => []

    onClear = Scope.ASYNC_ACTION
    onOperatorChange = Scope.ASYNC_ACTION_ONE<StringKeywordOperator>()
    onOptionSelected = Scope.ASYNC_ACTION_ONE<KeywordOption[]>()
    onReloadOptions = Scope.ASYNC_ACTION
}

@Observable
export class MultipleOptionStringKeywordFilterScope extends Scope implements IFilterScope {
    @observe() caption = ''
    // Fechado de saída, como o acordeão não controlado se comportava antes de o escopo passar
    // a mandar nele. Abre no clique do usuário ou quando um item é adicionado pelo +.
    @observe() expanded = false
    @observe() visible = false
    @observe() acceptBlankValues = false
    order = 0
    readonly conjunction = new ObservableArray<MultipleOptionStringKeywordParcelFilterScope>(this)
    readonly disjunction = new ObservableArray<MultipleOptionStringKeywordParcelFilterScope>(this)

    onAddFilter = Scope.ASYNC_ACTION_ONE<JunctionMode>()
    onExpandedChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onClearAllFilters = Scope.ASYNC_ACTION
}

export type KeywordOption = {
    key: string
    label: string
}

@Observable
export class StringKeywordParcelFilterScope extends Scope {
    uid = ''
    junctionMode: JunctionMode = JunctionMode.AND

    @observe() operator = StringKeywordOperator.is_in
    @observe() value = ''
    @observe() valueVisible = true
    @observe() extra?: Scope
    @observe() options: KeywordOption[] = []

    onClear = Scope.ASYNC_ACTION
    onEnterKeyPressed = Scope.ASYNC_ACTION
    onOperatorChange = Scope.ASYNC_ACTION_ONE<StringKeywordOperator>()
    onValueChange = Scope.ASYNC_ACTION_STRING
    onOptionSelected = Scope.ASYNC_ACTION_ONE<KeywordOption>()
}

@Observable
export class StringKeywordFilterScope extends Scope implements IFilterScope {
    @observe() caption = ''
    // Fechado de saída, como o acordeão não controlado se comportava antes de o escopo passar
    // a mandar nele. Abre no clique do usuário ou quando um item é adicionado pelo +.
    @observe() expanded = false
    @observe() visible = false
    @observe() acceptBlankValues = false
    order = 0
    readonly conjunction = new ObservableArray<StringKeywordParcelFilterScope>(this)
    readonly disjunction = new ObservableArray<StringKeywordParcelFilterScope>(this)

    onAddFilter = Scope.ASYNC_ACTION_ONE<JunctionMode>()
    onExpandedChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onClearAllFilters = Scope.ASYNC_ACTION
}

@Observable
export class NumberParcelFilterScope extends Scope {
    uid = ''
    junctionMode: JunctionMode = JunctionMode.AND

    toExclusive = false

    @observe() operator = NumberOperator.is
    @observe() value = ''
    @observe() toValue = ''
    @observe() valueVisible = true

    onClear = Scope.ASYNC_ACTION
    onEnterKeyPressed = Scope.ASYNC_ACTION
    onOperatorChange = Scope.ASYNC_ACTION_ONE<NumberOperator>()
    onValueChange = Scope.ASYNC_ACTION_TWO<string, 'from' | 'to'>()
}

@Observable
export class NumberFilterScope extends Scope implements IFilterScope {
    @observe() caption = ''
    // Fechado de saída, como o acordeão não controlado se comportava antes de o escopo passar
    // a mandar nele. Abre no clique do usuário ou quando um item é adicionado pelo +.
    @observe() expanded = false
    @observe() visible = false
    @observe() acceptBlankValues = false
    order = 0
    readonly conjunction = new ObservableArray<NumberParcelFilterScope>(this)
    readonly disjunction = new ObservableArray<NumberParcelFilterScope>(this)

    onAddFilter = Scope.ASYNC_ACTION_ONE<JunctionMode>()
    onExpandedChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onClearAllFilters = Scope.ASYNC_ACTION
}

@Observable
export class DateParcelFilterScope extends Scope {
    uid = ''
    junctionMode: JunctionMode = JunctionMode.AND

    @observe() value = ''
    @observe() toValue = ''
    @observe() valueVisible = true

    onClear = Scope.ASYNC_ACTION
    onEnterKeyPressed = Scope.ASYNC_ACTION
    onValueChange = Scope.ASYNC_ACTION_TWO<string, 'from' | 'to'>()
}

@Observable
export class DateFilterScope extends Scope implements IFilterScope {
    @observe() caption = ''
    // Fechado de saída, como o acordeão não controlado se comportava antes de o escopo passar
    // a mandar nele. Abre no clique do usuário ou quando um item é adicionado pelo +.
    @observe() expanded = false
    @observe() visible = false
    @observe() acceptBlankValues = false
    order = 0
    readonly conjunction = new ObservableArray<DateParcelFilterScope>(this)
    readonly disjunction = new ObservableArray<DateParcelFilterScope>(this)

    onAddFilter = Scope.ASYNC_ACTION_ONE<JunctionMode>()
    onExpandedChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onClearAllFilters = Scope.ASYNC_ACTION
}

@Observable
export class SelectParcelFilterScope extends Scope {
    uid = ''
    junctionMode: JunctionMode = JunctionMode.AND

    @observe() operator = SelectOperator.is
    @observe() value = ''
    @observe() valueVisible = true

    onClear = Scope.ASYNC_ACTION
    onEnterKeyPressed = Scope.ASYNC_ACTION
    onOperatorChange = Scope.ASYNC_ACTION_ONE<SelectOperator>()
    onValueChange = Scope.ASYNC_ACTION_TWO<string, 'from' | 'to'>()
}

@Observable
export class SelectFilterScope extends Scope implements IFilterScope {
    @observe() caption = ''
    // Fechado de saída, como o acordeão não controlado se comportava antes de o escopo passar
    // a mandar nele. Abre no clique do usuário ou quando um item é adicionado pelo +.
    @observe() expanded = false
    @observe() visible = false
    @observe() acceptBlankValues = false

    order = 0
    readonly conjunction = new ObservableArray<SelectParcelFilterScope>(this)
    readonly disjunction = new ObservableArray<SelectParcelFilterScope>(this)

    getOptions: () => FilterSelectOption[] = () => []
    onAddFilter = Scope.ASYNC_ACTION_ONE<JunctionMode>()
    onExpandedChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onClearAllFilters = Scope.ASYNC_ACTION
}

export type FilterCatalogTab = 'all' | 'mine' | 'shared' | 'fav'

@Observable
export class FilterCatalogScope extends Scope {
    @observe() openedId = ''
    @observe() openedTitle = ''
    @observe() openedGroup = ''
    @observe() openedOwner = false
    @observe() openedSummary = ''
    @observe() dirty = false

    @observe() libraryOpen = false
    @observe() tab: FilterCatalogTab = 'all'
    @observe() searchText = ''
    @observe() loading = false
    @observe() expandedIds: string[] = []
    @observe() expandedGroups: string[] = []

    @observe() askYesOrNo = ''

    getOptions: () => FilterCatalogOption[] = () => []
    getVisibleOptions: () => FilterCatalogOption[] = () => []
    getFieldLabels: (option: FilterCatalogOption) => string[] = () => []
    countByTab: (tab: FilterCatalogTab) => number = () => 0

    onToggleLibrary = Scope.ASYNC_ACTION_BOOLEAN
    onTabChange = Scope.ASYNC_ACTION_STRING
    onSearchChange = Scope.ASYNC_ACTION_STRING
    onToggleExpanded = Scope.ASYNC_ACTION_STRING
    onToggleGroup = Scope.ASYNC_ACTION_STRING
    onToggleFavorite = Scope.ASYNC_ACTION_ONE<FilterCatalogOption>()

    onApplyFilter = Scope.ASYNC_ACTION_STRING
    onCloseOpened = Scope.ASYNC_ACTION
    onSaveOpened = Scope.ASYNC_ACTION
    onDiscardChanges = Scope.ASYNC_ACTION

    onCreate = Scope.ASYNC_ACTION
    onSaveAsNew = Scope.ASYNC_ACTION
    onDuplicate = Scope.ASYNC_ACTION_ONE<FilterCatalogOption>()
    onRename = Scope.ASYNC_ACTION_ONE<FilterCatalogOption>()
    onShare = Scope.ASYNC_ACTION_ONE<FilterCatalogOption>()
    onDelete = Scope.ASYNC_ACTION_ONE<FilterCatalogOption>()
    onYesNoResponse = Scope.ASYNC_ACTION_BOOLEAN
}

export type FilterCatalogFormMode = 'create' | 'duplicate' | 'rename' | 'save-as-new'

export type FilterCatalogShareMode = 'me' | 'dept' | 'pick'

@Observable
export class FilterCatalogFormScope extends Scope {
    @observe() mode: FilterCatalogFormMode = 'create'
    @observe() group = ''
    @observe() title = ''
    @observe() groups: number[] = []
    @observe() users: number[] = []
    @observe() shareMode: FilterCatalogShareMode = 'me'
    @observe() duplicateOf = ''

    allGroups?: GroupRow[]
    allUsers?: Partial<UserRow>[]

    groupOptions: string[] = []

    onTitleChange = Scope.ASYNC_ACTION_STRING
    onGroupChange = Scope.ASYNC_ACTION_STRING
    onShareModeChange = Scope.ASYNC_ACTION_STRING
    onGroupsChange = Scope.ASYNC_ACTION_ONE<number[]>()
    onUsersChange = Scope.ASYNC_ACTION_ONE<number[]>()

    onClose = Scope.ASYNC_ACTION
    onSave = Scope.ASYNC_ACTION
}

@Observable
export class FilterCatalogShareScope extends Scope {
    @observe() title = ''
    @observe() groups: number[] = []
    @observe() users: number[] = []

    allGroups?: GroupRow[]
    allUsers?: Partial<UserRow>[]

    groupName: (id: number) => string = (id) => String(id)
    userName: (id: number) => string = (id) => String(id)

    onGroupsChange = Scope.ASYNC_ACTION_ONE<number[]>()
    onUsersChange = Scope.ASYNC_ACTION_ONE<number[]>()
    onClose = Scope.ASYNC_ACTION
    onSave = Scope.ASYNC_ACTION
}

@Observable
export class EnchenteRs2024Scope extends Scope {
    @observe() active = false
    onChange = Scope.ASYNC_ACTION_BOOLEAN
    onNotifyMap = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class FilterExtraCnpjScope extends Scope {
    @observe() cloning = false
    onModeChange = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class FilterPaneScope extends Scope {
    @observe() expanded = false

    readonly catalog = new FilterCatalogScope()
    readonly enchenteRs2024 = new EnchenteRs2024Scope()
    readonly nomeEmpresa = new StringFilterScope()
    readonly nomeFantasia = new StringFilterScope()
    readonly nomeMatriz = new StringFilterScope()
    readonly cnpj = new StringKeywordFilterScope()
    readonly cnpjMatriz = new StringKeywordFilterScope()
    readonly cnpjGrupo = new StringKeywordFilterScope()
    readonly dtInicioAtividade = new DateFilterScope()
    readonly situacaoCadastral = new MultipleOptionStringKeywordFilterScope()
    readonly dtSituacaoCadastral = new DateFilterScope()
    readonly dtCompetenciaInicioAtividade = new DateFilterScope()
    readonly naturezaJuridicaCodes = new MultipleOptionStringKeywordFilterScope()
    readonly naturezaJuridicaGrupoCodes = new MultipleOptionStringKeywordFilterScope()
    readonly naturezaJuridicaPublicaPrivadaCodes = new SelectFilterScope()
    readonly cnaeTexts = new StringFilterScope()
    readonly tipoCnaeTexts = new MultipleOptionStringKeywordFilterScope()
    readonly tipoEstabelecimentoSaudeCodes = new MultipleOptionStringKeywordFilterScope()
    readonly porteEstabelecimentoSaudeCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cnaeClasseCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cnaeClasseTexts = new StringFilterScope()
    readonly cnaeDivisaoCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cnaeDivisaoTexts = new StringFilterScope()
    readonly cnaeGrupoCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cnaeGrupoTexts = new StringFilterScope()
    readonly cnaeSecaoCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cnaeSecaoTexts = new StringFilterScope()
    readonly cnaeSetorCods = new MultipleOptionStringKeywordFilterScope()

    readonly cnesTexts = new StringKeywordFilterScope()
    readonly uf = new MultipleOptionStringKeywordFilterScope()
    readonly cidadeCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cboCodes = new MultipleOptionStringKeywordFilterScope()
    readonly cboTexts = new StringFilterScope()
    readonly pesCboCodes = new MultipleOptionStringKeywordFilterScope()
    readonly pesCboTexts = new StringFilterScope()
    readonly cboCategorias = new MultipleOptionStringKeywordFilterScope()
    readonly pesCboCategorias = new MultipleOptionStringKeywordFilterScope()
    readonly bairroTexts = new StringFilterScope()
    readonly regiaoMetropolitanaCodes = new MultipleOptionStringKeywordFilterScope()
    readonly scoreRiscos = new MultipleOptionStringKeywordFilterScope()

    readonly socioNome = new StringFilterScope()
    readonly prfNome = new StringFilterScope()
    readonly pesNome = new StringFilterScope()

    readonly totalEmpresaGrupo = new NumberFilterScope()
    readonly totalEmpresaCapSocial = new NumberFilterScope()
    readonly totalFuncionarios = new NumberFilterScope()
    readonly totalFuncionarios_0_a_18 = new NumberFilterScope()
    readonly totalFuncionarios_19_a_33 = new NumberFilterScope()
    readonly totalFuncionarios_34_a_48 = new NumberFilterScope()
    readonly totalFuncionarios_49_a_58 = new NumberFilterScope()
    readonly totalFuncionarios_59_ou_mais = new NumberFilterScope()
    readonly totalFuncionariosFemininos = new NumberFilterScope()
    readonly totalFuncionariosMasculinos = new NumberFilterScope()
    readonly totalFuncionariosDoGrupo = new NumberFilterScope()
    readonly faixaFaturamentoAnual = new NumberFilterScope()
    readonly faixaFaturamentoConsolidadoGrupo = new NumberFilterScope()
    readonly idadeEmpresa = new NumberFilterScope()
    readonly matrizFilial = new SelectFilterScope()
    readonly regimeTributario = new MultipleOptionStringKeywordFilterScope()
    readonly porteReceitaFederal = new MultipleOptionStringKeywordFilterScope()
    readonly regiaoGeograficaImediataCodes = new MultipleOptionStringKeywordFilterScope()
    readonly regiaoGeograficaIntermediariaCodes = new MultipleOptionStringKeywordFilterScope()
    readonly portePorFuncionarios = new MultipleOptionStringKeywordFilterScope()
    readonly portePorFaturamentoEstimadoCnpj = new MultipleOptionStringKeywordFilterScope()
    readonly portePorFaturamentoEstimadoGrupo = new MultipleOptionStringKeywordFilterScope()
    readonly cadeiaValorSaude = new SelectFilterScope()

    // colunas da listagem de empresas que não tinham filtro
    readonly matrizUfs = new MultipleOptionStringKeywordFilterScope()
    readonly cep = new StringKeywordFilterScope()
    readonly logradouro = new StringFilterScope()
    readonly enderecoNumeros = new StringKeywordFilterScope()
    readonly enderecoComplementoTexts = new StringFilterScope()
    readonly enderecoLatitude = new NumberFilterScope()
    readonly enderecoLongitude = new NumberFilterScope()
    readonly telefone1 = new StringFilterScope()
    readonly telefone2 = new StringFilterScope()
    readonly email = new StringFilterScope()
    readonly totalFiliais = new NumberFilterScope()
    readonly totalFuncionariosMatrizEFiliais = new NumberFilterScope()
    readonly salarioTotal = new NumberFilterScope()
    readonly salarioTotalGrupo = new NumberFilterScope()
    readonly optanteSimples = new MultipleOptionStringKeywordFilterScope()
    readonly optanteMei = new MultipleOptionStringKeywordFilterScope()
    readonly webNomeTexts = new StringFilterScope()
    readonly webCategoriasTexts = new StringFilterScope()
    readonly webNota = new NumberFilterScope()
    readonly webQtdAvaliacoes = new NumberFilterScope()
    readonly webTempoMedio = new NumberFilterScope()
    readonly webSite = new StringFilterScope()
    readonly webTelefone = new StringFilterScope()

    @observe() isFiltering = false
    @observe() searchFilterText = ''
    onSearchFilterChanged = Scope.ASYNC_ACTION_STRING

    onToggleExpanded = Scope.ASYNC_ACTION
    onClearFilters = Scope.ASYNC_ACTION
    onApply = Scope.ASYNC_ACTION
}

export type ListagemTabsOption = 'pj' | 'qsa' | 'prf' | 'pes'

@Observable
export class ListagemTabsScope extends Scope {
    @observe() active: ListagemTabsOption = 'pj'
    @observe() expanded = false
    empresasListagem = new TableEmpresaScope()
    qsaListagem = new TableQSAScope()
    prfListagem = new TablePRFScope()
    pesListagem = new TablePESScope()

    onSelect = Scope.ASYNC_ACTION_ONE<ListagemTabsOption>()
    onExpandClick = Scope.ASYNC_ACTION
    onShrunkClick = Scope.ASYNC_ACTION
}

@Observable
export class TheConsumerMarketScope extends Scope {
    header = new HeaderScope()
    filterPane = new FilterPaneScope()

    @observe() dataAtualizacao = ''
    @observe() dataDeCorte = ''

    @observe() appliedFilters: AppliedFilterSummary[] = []

    @observe() maximizeCard: 'mapa' | 'table' | '' = ''

    totalEmpresas = new ValueScope()
    totalEmpresasAtivas = new ValueScope()
    totalEmpresasNovasUltimoMes = new ValueScope()
    filialMatriz = new ChartFilialMatrizScope()
    situacaoCadastral = new ChartSituacaoCadastralScope()
    naturezaJuridica = new ChartNaturezaJuridicaScope()
    empresasAtivasPorUF = new ChartEmpresasAtivasPorUfScope()
    atividadeEconomica = new TableAtividadeEconomicaScope()
    abertasFechadas = new ChartEmpresasAbertasFechadasScope()
    empresasNoGrupo = new ChartFaixasScope()
    faixaCapitalSocial = new ChartFaixasScope()
    faixaDeIdade = new ChartFaixasScope()
    faixaDeFuncionarios = new ChartFaixasScope()
    faixaDeFuncionariosNoGrupo = new ChartFaixasScope()
    faixaDeFaturamentoAnual = new ChartFaixasScope()
    faixaDeFaturamentoConsolidadoGrupo = new ChartFaixasScope()

    listagemTabs = new ListagemTabsScope()

    mapa = new MapCardScope()

    readonly keyboard = {
        ctrlKey: false
    }

    onCtrlKeyToggled = Scope.ASYNC_ACTION_BOOLEAN
}

let DEFAULT_CENTER: L.LatLngLiteral = { lat: -15.7754462, lng: -53.7970891 }

export const ScopeDefaults = new (class {
    getDefaultCenter(): L.LatLngLiteral {
        return { ...DEFAULT_CENTER }
    }

    setDefaultCenter(value: L.LatLngLiteral) {
        DEFAULT_CENTER = value
    }
})()

export default TheConsumerMarketScope
